import { supabase } from '../lib/supabaseClient';
import { useSession } from '../providers/AuthProvider';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import DarkModeToggle from '@/components/ui/DarkModeToggle';

export function HeaderUserMenu() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  // Calculate menu position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + 8, // 8px = mt-2 equivalent
        right: window.innerWidth - rect.right,
      });
    } else {
      setMenuPosition(null);
    }
  }, [isOpen]);

  // Close menu on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        menuRef.current && 
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    // Delay to avoid immediate closure when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    sessionStorage.setItem('__manual_signout__', '1');
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleResetOnboarding = () => {
    if (user?.id) {
      localStorage.removeItem(`onboarding_completed:${user.id}`);
      localStorage.removeItem(`onboarding_checklist:${user.email}`);
      window.location.reload();
    }
  };

  if (!user) {
    return (
      <Link
        to="/login"
        className="rounded-xl px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
        style={{ backgroundColor: '#1A7A5E' }}
      >
        Log ind
      </Link>
    );
  }

  // Get user initials for avatar
  const email = user.email || '';
  const initials = email
    .split('@')[0]
    .split('.')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  const toggleMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className="relative" style={{ position: 'relative', zIndex: 100 }}>
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleMenu}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          className="flex items-center gap-2 rounded-xl px-3 py-2 transition-colors hover:bg-muted/50 active:bg-muted cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-app-focus focus-visible:ring-offset-2"
          style={{ 
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 100
          }}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label="User menu"
        >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary overflow-hidden flex-shrink-0">
          <img
            src="/logo.svg"
            alt="Tryg Hånd"
            className="h-6 w-6 object-contain"
            onError={(e) => {
              const img = e.currentTarget;
              img.style.display = 'none';
              const fallback = img.nextElementSibling as HTMLElement | null;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <span
            className="hidden items-center justify-center text-sm font-semibold text-primary-foreground"
            aria-hidden="true"
          >
            {initials}
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
            isOpen ? '' : 'rotate-180'
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
        </button>
      </div>

      {isOpen && menuPosition && createPortal(
        <div 
          ref={menuRef}
          className="fixed w-64 rounded-xl border border-border bg-card shadow-lg overflow-hidden"
          style={{ 
            zIndex: 10000,
            top: `${menuPosition.top}px`,
            right: `${menuPosition.right}px`,
            pointerEvents: 'auto'
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="border-b border-border px-4 py-3 bg-muted/30">
            <p className="text-sm font-medium text-foreground">
              {email}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {user.user_metadata?.role || user.app_metadata?.role || 'Bruger'}
            </p>
          </div>

          <div className="py-2">
            <Link
              to="/profile"
              onClick={() => setIsOpen(false)}
              data-tour="profile-link"
              className="flex items-center gap-3 px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
              Min profil
            </Link>

            <Link
              to="/dashboard"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-3 px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              Overblik
            </Link>

            <button
              onClick={() => {
                handleResetOnboarding();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Nulstil velkomstguide
            </button>
          </div>

          <div className="border-t border-border px-4 py-2 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Tema</span>
            <DarkModeToggle />
          </div>

          <div className="border-t border-border py-2">
            <button
              onClick={() => {
                handleLogout();
                setIsOpen(false);
              }}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-destructive transition-colors hover:bg-muted"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Log ud
            </button>
          </div>
        </div>
      , document.body)}
    </>
  );
}
